/**
 * useChat hook
 * Manages chat state and streaming responses
 */
import { useState, useCallback } from 'react';
import { LlmProxyService } from '../../../services/llm-proxy.service.js';
import type { ChatMessage, CompletionMessage } from '../../../types/llm.types.js';

export function useChat(service: LlmProxyService) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const sendMessage = useCallback(
        async (content: string, model: string) => {
            if (!content.trim() || isStreaming) return;

            // Add user message
            const userMessage: ChatMessage = {
                id: `user-${Date.now()}`,
                role: 'user',
                content: content.trim(),
                timestamp: new Date(),
            };

            setMessages((prev) => [...prev, userMessage]);
            setError(null);
            setIsStreaming(true);

            // Create assistant message placeholder
            const assistantMessageId = `assistant-${Date.now()}`;
            const assistantMessage: ChatMessage = {
                id: assistantMessageId,
                role: 'assistant',
                content: '',
                timestamp: new Date(),
            };

            setMessages((prev) => [...prev, assistantMessage]);

            try {
                // Convert chat messages to completion messages
                const completionMessages: CompletionMessage[] = [
                    ...messages.map((m) => ({
                        role: m.role,
                        content: m.content,
                    })),
                    {
                        role: 'user' as const,
                        content: content.trim(),
                    },
                ];

                // Stream response
                let fullContent = '';
                for await (const chunk of service.chatStream(completionMessages, model)) {
                    fullContent += chunk;

                    // Update message content incrementally
                    setMessages((prev) =>
                        prev.map((msg) =>
                            msg.id === assistantMessageId
                                ? { ...msg, content: fullContent }
                                : msg
                        )
                    );
                }

                setIsStreaming(false);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                setError(errorMessage);

                // Update last message with error
                setMessages((prev) =>
                    prev.map((msg) =>
                        msg.id === assistantMessageId
                            ? { ...msg, content: `Error: ${errorMessage}` }
                            : msg
                    )
                );

                setIsStreaming(false);
            }
        },
        [service, messages, isStreaming]
    );

    const clearMessages = useCallback(() => {
        setMessages([]);
        setError(null);
    }, []);

    return {
        messages,
        isStreaming,
        error,
        sendMessage,
        clearMessages,
    };
}
